import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiCalendar, FiInfo, FiX } from "react-icons/fi";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import Modal from "../components/ui/Modal";
import Select from "../components/ui/Select";
import Input from "../components/ui/Input";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import EmptyState from "../components/ui/EmptyState";
import { leaveApi, LeaveBalance, LeaveRequest } from "../api/leave";
import { format } from "date-fns";
import { fr } from "date-fns/locale/fr";
import toast from "react-hot-toast";

const LEAVE_TYPE_OPTIONS = [
  { value: "conges_payes", label: "Congés payés" },
  { value: "rtt", label: "RTT" },
  { value: "maladie", label: "Maladie" },
  { value: "sans_solde", label: "Sans solde" },
  { value: "autre", label: "Autre" },
];

const STATUS_VARIANT: Record<
  LeaveRequest["status"],
  "warning" | "success" | "danger" | "neutral"
> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "neutral",
};

const MesAbsencesPage: React.FC = () => {
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [leaveType, setLeaveType] = useState("conges_payes");
  const [leaveBalanceId, setLeaveBalanceId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [comment, setComment] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [balancesRes, requestsRes] = await Promise.all([
        leaveApi.listMyBalances(),
        leaveApi.listMyRequests(),
      ]);
      setBalances(balancesRes.data.data?.balances ?? []);
      setRequests(requestsRes.data.data?.requests ?? []);
    } catch {
      toast.error("Impossible de charger vos absences");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalDisponible = useMemo(
    () => balances.reduce((sum, s) => sum + s.estimated_balance, 0),
    [balances],
  );

  const balanceOptions = useMemo(() => {
    const filtered = balances.filter((b) => {
      if (leaveType === "conges_payes") return b.leave_type === "conges_payes";
      if (leaveType === "rtt") return b.leave_type === "rtt";
      return false;
    });
    return [
      { value: "", label: "Compte par défaut" },
      ...filtered.map((b) => ({
        value: String(b.id),
        label: `${b.account_label} (${b.estimated_balance.toFixed(2)} j)`,
      })),
    ];
  }, [balances, leaveType]);

  const needsBalance = leaveType === "conges_payes" || leaveType === "rtt";

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      toast.error("Indiquez les dates de début et de fin");
      return;
    }
    setSubmitting(true);
    try {
      await leaveApi.createRequest({
        leave_type: leaveType,
        start_date: startDate,
        end_date: endDate,
        leave_balance_id: leaveBalanceId ? Number(leaveBalanceId) : undefined,
        comment: comment.trim() || undefined,
      });
      toast.success("Demande envoyée au service RH");
      setShowForm(false);
      setStartDate("");
      setEndDate("");
      setComment("");
      setLeaveBalanceId("");
      await loadData();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Échec de l'envoi de la demande";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (request: LeaveRequest) => {
    if (!window.confirm("Annuler cette demande ?")) return;
    try {
      await leaveApi.cancelRequest(request.id);
      toast.success("Demande annulée");
      await loadData();
    } catch {
      toast.error("Impossible d'annuler la demande");
    }
  };

  if (loading) {
    return (
      <div
        style={{ padding: "3rem", display: "flex", justifyContent: "center" }}
      >
        <LoadingSpinner size={36} />
      </div>
    );
  }

  return (
    <div style={{ padding: "2rem", maxWidth: 960, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "1.5rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <h1
            style={{
              margin: "0 0 0.35rem",
              fontSize: "var(--font-size-2xl)",
              fontWeight: 700,
              color: "var(--color-primary)",
            }}
          >
            Demande d&apos;absence
          </h1>
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
            }}
          >
            Posez vos congés : validation par le service RH
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          icon={<FiInfo size={14} />}
          onClick={() => toast("Politique de congés : configurable par la RH")}
        >
          Politique de congés
        </Button>
      </div>

      <Card
        padding="1.5rem"
        style={{
          marginBottom: "1.5rem",
          background: "linear-gradient(135deg, #E0F2FE 0%, #ECFEFF 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-2xl)",
              fontWeight: 700,
              color: "var(--color-brand-dark)",
            }}
          >
            {totalDisponible.toLocaleString("fr-FR", {
              minimumFractionDigits: 1,
            })}{" "}
            jours disponibles
          </p>
          <p
            style={{
              margin: "0.25rem 0 0",
              fontSize: "var(--font-size-sm)",
              color: "var(--color-text-secondary)",
            }}
          >
            à ce jour (congés et RTT)
          </p>
        </div>
        <Button
          icon={<FiCalendar size={16} />}
          onClick={() => setShowForm(true)}
        >
          Nouvelle demande
        </Button>
      </Card>

      <h2
        style={{
          margin: "0 0 1rem",
          fontSize: "var(--font-size-base)",
          fontWeight: 600,
        }}
      >
        Vos soldes de congés
      </h2>
      <Card padding="0" style={{ overflow: "hidden", marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr repeat(3, 1fr)",
            gap: "0.5rem",
            padding: "0.75rem 1.25rem",
            background: "var(--color-bg)",
            fontSize: "var(--font-size-xs)",
            fontWeight: 600,
            color: "var(--color-text-secondary)",
          }}
        >
          <span>Compte</span>
          <span style={{ textAlign: "right" }}>Solde actuel</span>
          <span style={{ textAlign: "right" }}>Jours posés</span>
          <span style={{ textAlign: "right" }}>Solde estimé</span>
        </div>
        {balances.length === 0 ? (
          <div style={{ padding: "1.5rem" }}>
            <EmptyState
              title="Aucun solde configuré"
              description="Contactez le service RH."
            />
          </div>
        ) : (
          balances.map((solde) => (
            <div
              key={solde.id}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr repeat(3, 1fr)",
                gap: "0.5rem",
                padding: "1rem 1.25rem",
                borderTop: "1px solid var(--color-border-light)",
                alignItems: "center",
                fontSize: "var(--font-size-sm)",
              }}
            >
              <div>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  {solde.account_label}
                </p>
                {solde.expiry_date && (
                  <p
                    style={{
                      margin: "0.15rem 0 0",
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-text-muted)",
                    }}
                  >
                    À poser avant le{" "}
                    {format(new Date(solde.expiry_date), "dd/MM/yyyy")}
                  </p>
                )}
              </div>
              <span style={{ textAlign: "right" }}>
                {solde.balance.toFixed(2)}
              </span>
              <span style={{ textAlign: "right" }}>
                {solde.pending_days.toFixed(2)}
              </span>
              <span style={{ textAlign: "right", fontWeight: 600 }}>
                {solde.estimated_balance.toFixed(2)}
              </span>
            </div>
          ))
        )}
      </Card>

      <h2
        style={{
          margin: "0 0 1rem",
          fontSize: "var(--font-size-base)",
          fontWeight: 600,
        }}
      >
        Vos demandes
      </h2>
      <Card padding="0" style={{ overflow: "hidden" }}>
        {requests.length === 0 ? (
          <div style={{ padding: "1.5rem" }}>
            <EmptyState
              title="Aucune demande"
              description="Créez une demande d'absence pour la soumettre au service RH."
              actionLabel="Nouvelle demande"
              onAction={() => setShowForm(true)}
            />
          </div>
        ) : (
          requests.map((req, index) => (
            <div
              key={req.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                padding: "1rem 1.25rem",
                borderTop:
                  index > 0 ? "1px solid var(--color-border-light)" : undefined,
                flexWrap: "wrap",
              }}
            >
              <div style={{ flex: 1, minWidth: 200 }}>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  {req.leave_type_label}
                  {" - "}
                  {format(new Date(req.start_date), "d MMM yyyy", {
                    locale: fr,
                  })}
                  {" → "}
                  {format(new Date(req.end_date), "d MMM yyyy", { locale: fr })}
                </p>
                <p
                  style={{
                    margin: "0.2rem 0 0",
                    fontSize: "var(--font-size-xs)",
                    color: "var(--color-text-muted)",
                  }}
                >
                  {req.days_count} jour{req.days_count > 1 ? "s" : ""} ouvré
                  {req.days_count > 1 ? "s" : ""}
                  {req.account_label ? ` · ${req.account_label}` : ""}
                </p>
                {req.rh_comment && req.status === "rejected" && (
                  <p
                    style={{
                      margin: "0.35rem 0 0",
                      fontSize: "var(--font-size-xs)",
                      color: "var(--color-danger)",
                    }}
                  >
                    Motif RH : {req.rh_comment}
                  </p>
                )}
              </div>
              <Badge variant={STATUS_VARIANT[req.status]} size="sm">
                {req.status_label}
              </Badge>
              {req.status === "pending" && (
                <button
                  type="button"
                  title="Annuler"
                  onClick={() => handleCancel(req)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "var(--color-text-muted)",
                    cursor: "pointer",
                  }}
                >
                  <FiX size={18} />
                </button>
              )}
            </div>
          ))
        )}
      </Card>

      <div
        style={{
          marginTop: "1.5rem",
          padding: "1rem",
          background: "var(--color-info-light)",
          borderRadius: "var(--radius-lg)",
          fontSize: "var(--font-size-sm)",
          color: "var(--color-text-secondary)",
        }}
      >
        <Badge variant="info" size="sm">
          Information
        </Badge>
        <p style={{ margin: "0.5rem 0 0", lineHeight: 1.6 }}>
          Vos demandes sont transmises au service RH pour validation. Les jours
          en attente sont déduits de votre solde estimé.
        </p>
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Nouvelle demande d'absence"
        footer={
          <>
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Envoi…" : "Envoyer la demande"}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Select
            label="Type d'absence"
            value={leaveType}
            onChange={(e) => {
              setLeaveType(e.target.value);
              setLeaveBalanceId("");
            }}
            options={LEAVE_TYPE_OPTIONS}
          />
          {needsBalance && balanceOptions.length > 1 && (
            <Select
              label="Compte de congés"
              value={leaveBalanceId}
              onChange={(e) => setLeaveBalanceId(e.target.value)}
              options={balanceOptions}
            />
          )}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.75rem",
            }}
          >
            <Input
              label="Date de début"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="Date de fin"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "0.35rem",
                fontSize: "var(--font-size-sm)",
                fontWeight: 500,
              }}
            >
              Commentaire (optionnel)
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                fontSize: "var(--font-size-sm)",
                border: "1px solid var(--color-border)",
                borderRadius: "var(--radius-md)",
                fontFamily: "var(--font-family)",
                resize: "vertical",
              }}
              placeholder="Précisions pour le service RH…"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default MesAbsencesPage;
